<template>
    <v-row dense>
        <v-col v-for="tile in tiles" :key="tile.label" cols="12" sm="6" lg="3">
            <v-card class="pavo-stat-tile">
                <v-card-text>
                    <div class="pavo-stat-tile-label">{{ tile.label }}</div>
                    <div class="pavo-stat-tile-value">{{ tile.value }}</div>
                    <div class="pavo-stat-tile-sub">{{ tile.sub }}</div>
                    <div class="pavo-stat-tile-bar" :class="`pavo-module-${tile.module}`" />
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { Dashboard } from "@a/dashboard";
import { formatCount, formatCurrency, toIsoDate } from "./format";

type Props = {
    dashboard: Dashboard;
};

const props = defineProps<Props>();

type Tile = {
    label: string;
    value: string;
    sub: string;
    module: "events" | "registration";
};

const weekRange = computed(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - today.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end };
});

const functionsThisWeek = computed(() => {
    const from = toIsoDate(weekRange.value.start);
    const to = toIsoDate(weekRange.value.end);
    return props.dashboard.functionCounts
        .filter((day) => day.date >= from && day.date <= to)
        .reduce((sum, day) => sum + day.count, 0);
});

function shortDay(date: Date): string {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const tiles = computed<Tile[]>(() => {
    const dashboard = props.dashboard;
    const inHouseNames = dashboard.inHouseConferences
        .map((conference) => conference.name ?? conference.code ?? "")
        .filter((name) => name !== "");
    const stats = dashboard.registrationStats;
    return [
        {
            label: "In house now",
            value: formatCount(dashboard.inHouseConferences.length),
            sub: inHouseNames.length > 0 ? inHouseNames.join(" · ") : "No conferences in house",
            module: "events",
        },
        {
            label: "Functions this week",
            value: formatCount(functionsThisWeek.value),
            sub: `${shortDay(weekRange.value.start)} - ${shortDay(weekRange.value.end)}`,
            module: "events",
        },
        {
            label: "Registrants",
            value: formatCount(stats.totalIndividuals),
            sub: `${formatCount(stats.activeRegistrations)} active registrations`,
            module: "registration",
        },
        {
            label: "Balance outstanding",
            value: formatCurrency(stats.outstandingBalance),
            sub: "unpaid registration fees",
            module: "registration",
        },
    ];
});
</script>

<style lang="scss">
.pavo-stat-tile-label {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: rgba(var(--v-theme-on-surface), 0.6);
}

.pavo-stat-tile-value {
    font-size: 30px;
    font-weight: 700;
    margin-top: 4px;
    font-variant-numeric: tabular-nums;
}

.pavo-stat-tile-sub {
    font-size: 12.5px;
    color: rgba(var(--v-theme-on-surface), 0.6);
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pavo-stat-tile-bar {
    height: 3px;
    border-radius: 2px;
    margin-top: 12px;

    &.pavo-module-events {
        background: rgb(var(--v-theme-primary));
    }

    &.pavo-module-registration {
        background: rgb(var(--v-theme-secondary));
    }
}
</style>
